/**
 * Map Chips DOM Component
 *
 * Creates and manages the map chip bar — a container holding all 4 chip buttons
 * (Available, Model, Coming Soon, Sold). Uses event delegation on the container for click
 * handling. No Redux, no business logic.
 *
 * @module nativeInteractiveSiteMap/components/mapChips.dom
 */

import createMapChipDom from './mapChip.dom';
import { MAP_CHIPS } from '../services/mapChipService';

const WRAPPER_CLASS = 'nhs-c-native-interactive-site-map__chips-wrapper';

/**
 * @param {Record<string, number>} initialCounts - chipKey -> count
 * @param {string} initialActiveChip - chipKey of the initially active chip
 * @returns {{ getElement, attachEventListeners, setActiveTab, updateCounts, remove }}
 */
export default function createMapChipsDom(initialCounts, initialActiveChip) {
  const state = {
    root: null,
    chips: new Map(), // chipKey -> mapChip.dom instance
    clickHandler: null,
  };

  function initializeDom() {
    state.root = document.createElement('div');
    state.root.className = WRAPPER_CLASS;
    state.root.dataset.qa = 'native_ism_mapchips';

    MAP_CHIPS.forEach((chip) => {
      const chipDom = createMapChipDom({
        label: chip.label,
        count: initialCounts?.[chip.key] ?? 0,
        chipKey: chip.key,
        isActive: chip.key === initialActiveChip,
      });
      state.chips.set(chip.key, chipDom);
      state.root.appendChild(chipDom.getElement());
    });
  }

  initializeDom();

  return {
    getElement() {
      return state.root;
    },

    /**
     * @param {object} handlers
     * @param {Function} handlers.onChipClick - Called with { chipKey } on chip click
     */
    attachEventListeners(handlers) {
      if (typeof handlers.onChipClick === 'function') {
        state.clickHandler = (event) => {
          const button = event.target.closest('[data-filter]');
          if (button && state.root.contains(button)) {
            handlers.onChipClick({ chipKey: button.dataset.filter });
          }
        };
        state.root.addEventListener('click', state.clickHandler);
      }
    },

    /**
     * Marks one chip active and all others inactive.
     * @param {string} chipKey
     */
    setActiveTab(chipKey) {
      state.chips.forEach((chip, value) => {
        chip.setActive(value === chipKey);
      });
    },

    /**
     * Updates count text for each chip whose chipKey appears in the map, and hides any chip
     * whose count is 0 — a chip with nothing to show adds no value. Single entry point for both
     * the initial reveal (once counts are known) and any later count change, so there is only
     * one place that decides chip visibility.
     * @param {Record<string, number>} counts - e.g. { available: 26, model: 2 }
     */
    updateCounts(counts) {
      Object.entries(counts ?? {}).forEach(([chipKey, count]) => {
        const chip = state.chips.get(chipKey);
        chip?.setCount(count);
        chip?.setVisible(count > 0);
      });
    },

    remove() {
      if (state.clickHandler) {
        state.root?.removeEventListener('click', state.clickHandler);
        state.clickHandler = null;
      }
      state.chips.forEach((chip) => chip.remove());
      state.chips.clear();
      state.root?.remove();
      state.root = null;
    },
  };
}
